import { createContext, useContext, ReactNode, useMemo } from "react";
import { useCommissionsData } from "@/hooks/useCommissionsData";
import { useAppStore } from "@/stores";
import { NestedUser, NestedCommission, SignedInCompany } from "@revelate/types";

import useSWR from "swr";
import { get, TableType } from "@/lib/supabase";

// Define a type for the serialized company without users to avoid circular references
type SerializedCompany = Omit<SignedInCompany, "users"> & { users?: undefined };

interface CommissionsContextType {
  company: SerializedCompany | undefined;
  users: NestedUser[] | undefined;
  projectedUsers: NestedUser[] | undefined;
  error: string | null;
  isLoading: boolean;
  progress: number;
  mutate: (updatedCommissions?: NestedCommission[]) => Promise<void>;
}

const CommissionsContext = createContext<CommissionsContextType | undefined>(
  undefined
);

export function CommissionsProvider({ children }: { children: ReactNode }) {
  const { currentUser, dateRange } = useAppStore((state) => state);

  const {
    data: company,
    error: companyError,
    isLoading: companyLoading,
  } = useSWR(
    [
      "companies" as TableType,
      currentUser ? currentUser?.company_id?.toString() : null,
    ],
    ([table, companyId]): Promise<SignedInCompany> => get(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Get user IDs from company data - memoize to prevent unnecessary re-renders
  const userIds = useMemo(() => {
    return (
      company?.users
        ?.filter((user) => user.include_in_calculations)
        .map((user) => user.id) || []
    );
  }, [company]);

  // Fetch actual commissions data directly from the database
  const {
    users,
    error: usersError,
    isLoading: usersLoading,
    mutate: mutateCommissions,
  } = useCommissionsData({
    currentUser,
    dateRange,
    userIds,
    enabled: true,
  });

  // Create a serializable version of the company to avoid circular references
  const serializedCompany = useMemo(() => {
    return company
      ? {
          ...company,
          // Remove circular references that could cause infinite depth
          users: undefined, // We're already providing users separately
        }
      : undefined;
  }, [company]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      company: serializedCompany,
      users,
      projectedUsers: undefined, // Removed projected users since we're not calculating anymore
      error: usersError || companyError,
      isLoading: companyLoading || usersLoading,
      progress: 100, // Since we're not streaming calculations, progress is always 100
      mutate: async (updatedCommissions?: NestedCommission[]) => {
        if (updatedCommissions) {
          // Use the mutate function from useCommissionsData
          await mutateCommissions(updatedCommissions);
        }
      },
    }),
    [
      serializedCompany,
      users,
      usersError,
      companyError,
      companyLoading,
      usersLoading,
      mutateCommissions,
    ]
  );

  return (
    <CommissionsContext.Provider value={value}>
      {children}
    </CommissionsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCommissions() {
  const context = useContext(CommissionsContext);
  if (context === undefined) {
    throw new Error("useCommissions must be used within a CommissionsProvider");
  }
  return context;
}
